import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import { AuthButton, AuthError, AuthPageShell } from '../features/user-dashboard/auth/AuthPageLayout';
import { acceptWorkspaceInvite, apiError, previewWorkspaceInvite } from '../features/drone-processing/api';

function loginPath(token: string) {
  sessionStorage.setItem('digisaka_post_login', `/workspace-invite?token=${encodeURIComponent(token)}`);
}

function sameEmail(left: string, right: string) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export default function WorkspaceInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const token = (searchParams.get('token') || '').trim();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [allowedEmail, setAllowedEmail] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(true);

  useEffect(() => {
    if (!token) {
      setPreviewing(false);
      return;
    }
    let cancelled = false;
    previewWorkspaceInvite(token)
      .then((preview) => {
        if (cancelled) return;
        setAllowedEmail(preview.email);
        setWorkspaceName(preview.workspace_name);
      })
      .catch((err) => {
        if (!cancelled) setPreviewError(apiError(err));
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const goSignIn = () => {
    if (!token) return;
    loginPath(token);
    if (isAuthenticated) logout();
    else navigate('/userdashboard');
  };

  const join = async () => {
    if (!token || !user || !sameEmail(user.email, allowedEmail)) return;
    setBusy(true);
    setError('');
    try {
      const result = await acceptWorkspaceInvite(token);
      const params = new URLSearchParams({ open: 'drone-processing' });
      if (result.workspace_id) params.set('workspace', result.workspace_id);
      navigate(`/?${params.toString()}`, { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading || previewing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#008540] border-t-transparent" />
      </div>
    );
  }

  if (!token || previewError) {
    return (
      <AuthPageShell title="Invite unavailable" subtitle="This link may have expired or already been used.">
        <AuthError message={previewError || 'Open the link from the workspace email.'} />
      </AuthPageShell>
    );
  }

  const signedIn = isAuthenticated && user && !user.is_system_guest;
  const allowed = signedIn && sameEmail(user.email, allowedEmail);

  return (
    <AuthPageShell
      title="Join a drone workspace"
      subtitle={workspaceName ? `Only ${allowedEmail} can join ${workspaceName}.` : `Only ${allowedEmail} can join.`}
    >
      {error && <AuthError message={error} />}
      {allowed ? (
        <>
          <p className="mb-6 text-center text-sm text-slate-600">
            Signed in as <strong>{user.email}</strong>
          </p>
          <AuthButton type="button" onClick={() => void join()} loading={busy} disabled={busy}>
            Join workspace
          </AuthButton>
        </>
      ) : (
        <>
          <p className="mb-6 text-center text-sm text-slate-600">
            {signedIn
              ? `You are signed in as ${user.email}. This invite cannot be used by that account.`
              : 'Sign in with that Gmail. If you do not have a Digisaka account yet, create one with the same address, then open this link again.'}
          </p>
          <AuthButton type="button" onClick={goSignIn}>
            {signedIn ? 'Sign in with the invited Gmail' : 'Sign in'}
          </AuthButton>
          {!signedIn && (
            <button
              type="button"
              onClick={() => {
                loginPath(token);
                navigate('/userdashboard?view=signup');
              }}
              className="mt-4 w-full text-center text-sm font-medium text-[#008540]"
            >
              Create an account
            </button>
          )}
        </>
      )}
    </AuthPageShell>
  );
}
